import NurseRosterList from "@components/feature/rosterTable/row_NurseRosterList";
import { useEffect, useMemo, useRef, useState, type ComponentProps } from "react";
import type * as type from "@vTypes";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";

import { images } from "@img/imagesMap";
import type { CellViolationInfo } from "@/services/validation";
import type { ShiftLookupMaps } from "@/utils/shift-util";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";
import { nurseGroupSortKey } from "@utils/common-util.ts";
import type { EmergencyBulkPeriodPick, EmergencyPostApplyHighlight, EmergencyReplacementSlotPick } from "@/types/emergency-replacement.types";
import { useRowSwapFlipAnimation } from "@/hooks/useRowSwapFlipAnimation";
import {
  canSwapRows,
  getReorderRowId,
  isRowSequenceBlocked,
  type RowMoveDirection,
} from "./rowSequenceReorderUtils";
import {
  isDispatchedDisplayRow,
  splitDispatchedRows,
} from "@/components/feature/nurse-membership/splitDispatchedRows";

export default function TableBody_team({
  team,
  nurses,
  copyNurses,
  availableShifts,
  shiftLookupMaps,
  currentSubmittedList,
  isEditMode,
  selectedShift,
  modDayShift,
  cellViolationMap,
  statsColumnShiftIds,
  statsDisplayShifts,
  prevTailData,
  showPerShiftStats = false,
  selectIdForSideprofile,
  scheduleYear = 0,
  scheduleMonth = 0,
  schedulePayloadYear,
  schedulePayloadMonth,
  currentGroupId = null,
  isEmergencyPickMode = false,
  onEmergencySlotPick,
  emergencySlotPick = null,
  isEmergencyBulkPickMode = false,
  onEmergencyBulkPick,
  emergencyBulkPeriodPick = null,
  emergencyBulkSelectedSteps = null,
  emergencyReplacementCandidateNurseId = null,
  emergencyPostApplyHighlight = null,
  enableRowSequenceReorder = false,
  onMoveNurseRow,
}: {
  team: type.FetchTeams;
  nurses: Array<ComponentProps<typeof NurseRosterList>["nurse"] | null>;
  copyNurses?: Array<{
    nurse_id?: string | number;
    id?: string | number;
  } | null>;
  availableShifts: type.availableShifts[];
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>;
  currentSubmittedList?: string[];
  isEditMode: boolean;
  selectedShift: type.Type_availableShifts | null;
  modDayShift: (
    nurseId: string,
    scheduleDay: number,
    changeShift: string,
    cellShiftDbId?: number | null
  ) => void;
  prevTailData?: PrevTailData | null;
  cellViolationMap?: Map<string, CellViolationInfo>;
  statsColumnShiftIds?: string[];
  statsDisplayShifts?: type.availableShifts[];
  showPerShiftStats?: boolean;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  scheduleYear?: number;
  scheduleMonth?: number;
  schedulePayloadYear?: number;
  schedulePayloadMonth?: number;
  currentGroupId?: string | null;
  isEmergencyPickMode?: boolean;
  onEmergencySlotPick?: (pick: EmergencyReplacementSlotPick) => void;
  emergencySlotPick?: EmergencyReplacementSlotPick | null;
  isEmergencyBulkPickMode?: boolean;
  onEmergencyBulkPick?: (pick: {
    nurseId: string;
    nurseName: string;
    dayIndex: number;
    dateISO: string;
    shiftColor?: string;
  }) => void;
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  emergencyBulkSelectedSteps?: Array<{ date: string; nurseId: string }> | null;
  emergencyReplacementCandidateNurseId?: string | null;
  emergencyPostApplyHighlight?: EmergencyPostApplyHighlight | null;
  enableRowSequenceReorder?: boolean;
  onMoveNurseRow?: (
    nurseId: string,
    direction: RowMoveDirection,
    visibleNurseIds: readonly string[],
    captureRowPositions?: () => void
  ) => void;
}) {
  const [isExpendedContents, setIsExpendedContents] = useState<boolean>(true);
  const rowSwapContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const clickWindow = (e: Event) => {
      const clickTarget = e.target as HTMLElement;
      if (!clickTarget.closest(".preceptorMark")) {
        document
          .querySelectorAll(".preceptorGroup")
          .forEach((tr) => tr.classList.remove("click"));
      }
    };
    window.addEventListener("click", clickWindow);
    return () => {
      window.removeEventListener("click", clickWindow);
    };
  }, []);

  const teamNurses = useMemo(
    () => splitDispatchedRows(nurses.filter((nurse) => nurse != null)).displayRows,
    [nurses]
  );
  const visibleNurseIds = useMemo(
    () => teamNurses.map((nurse) => getReorderRowId(nurse)),
    [teamNurses]
  );
  const captureRowPositions = useRowSwapFlipAnimation(
    rowSwapContainerRef,
    visibleNurseIds
  );

  const nurseList = teamNurses.map((nurse, index: number) => {
    const nurseId = getReorderRowId(nurse);
    const isSubmitted = currentSubmittedList?.includes(nurseId) ?? false;
    const previousNurse = teamNurses[index - 1];
    const nextNurse = teamNurses[index + 1];
    const roleGroupBottomBorder =
      nextNurse != null &&
      nurseGroupSortKey(nurse) !== nurseGroupSortKey(nextNurse);
    const dispatchGroupTopBorder =
      isDispatchedDisplayRow(nurse) &&
      (previousNurse == null || !isDispatchedDisplayRow(previousNurse));
    const showRowReorder =
      enableRowSequenceReorder &&
      onMoveNurseRow != null &&
      !isRowSequenceBlocked(nurse);
    const canMoveRowUp =
      showRowReorder && previousNurse != null && canSwapRows(nurse, previousNurse);
    const canMoveRowDown =
      showRowReorder && nextNurse != null && canSwapRows(nurse, nextNurse);

    const nurseKey = nurseId;
    const globalNurseIndex =
      Array.isArray(copyNurses) && nurseKey.length > 0
        ? copyNurses.findIndex(
            (n) => n != null && String(n?.nurse_id ?? n?.id ?? "") === nurseKey
          )
        : -1;
    const nursesIndexForRow = globalNurseIndex >= 0 ? globalNurseIndex : index;

    return (
      <NurseRosterList
        key={nurseId || `nurse-row-${index}`}
        isEditMode={isEditMode}
        issuedRosterView={false}
        nursesIndex={nursesIndexForRow}
        displayRowNumber={index + 1}
        nurse={nurse}
        availableShifts={availableShifts}
        shiftLookupMaps={shiftLookupMaps}
        isSubmitted={isSubmitted}
        modDayShift={modDayShift}
        selectedShift={selectedShift}
        cellViolationMap={cellViolationMap}
        statsColumnShiftIds={statsColumnShiftIds}
        statsDisplayShifts={statsDisplayShifts}
        prevTailData={prevTailData}
        showPerShiftStats={showPerShiftStats}
        roleGroupBottomBorder={roleGroupBottomBorder}
        dispatchGroupTopBorder={dispatchGroupTopBorder}
        showRowReorder={showRowReorder}
        canMoveRowUp={canMoveRowUp}
        canMoveRowDown={canMoveRowDown}
        onMoveRowUp={() =>
          onMoveNurseRow?.(nurseId, "up", visibleNurseIds, captureRowPositions)
        }
        onMoveRowDown={() =>
          onMoveNurseRow?.(
            nurseId,
            "down",
            visibleNurseIds,
            captureRowPositions
          )
        }
        selectIdForSideprofile={selectIdForSideprofile}
        scheduleYear={scheduleYear}
        scheduleMonth={scheduleMonth}
        schedulePayloadYear={schedulePayloadYear}
        schedulePayloadMonth={schedulePayloadMonth}
        currentGroupId={currentGroupId}
        isEmergencyPickMode={isEmergencyPickMode}
        onEmergencySlotPick={onEmergencySlotPick}
        emergencySlotPick={emergencySlotPick}
        isEmergencyBulkPickMode={isEmergencyBulkPickMode}
        onEmergencyBulkPick={onEmergencyBulkPick}
        emergencyBulkPeriodPick={emergencyBulkPeriodPick}
        emergencyBulkSelectedSteps={emergencyBulkSelectedSteps}
        emergencyReplacementCandidateNurseId={
          emergencyReplacementCandidateNurseId
        }
        emergencyPostApplyHighlight={emergencyPostApplyHighlight}
      />
    );
  });

  return (
    <div className="tbody tbody_team">
      <div
        className={`tbody_team_header ${isExpendedContents ? "" : "tbody_team_header--closed"}`}
      >
        <div className="nurseCount padding_hover_bg_vertical">
          <img
            src={images.user}
            alt=""
            className=""
            style={{ height: "1em" }}
          />
          {nurses.length}
        </div>

        <div className="tbody_team_teamName">
          <button
            type="button"
            style={{ width: "100%", height: "100%" }}
            onClick={() => {
              setIsExpendedContents((prev) => !prev);
            }}
          >
            {team.team_name}
          </button>
        </div>
      </div>

      {isExpendedContents && (
        <div ref={rowSwapContainerRef} className="tbody_scollbox">
          {nurseList.some((item: unknown) => item !== null) ? (
            nurseList
          ) : (
            <div className="comp_tr comp_tr--oneLine">
              그룹이 설정된 인원이 없습니다.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
